import UserRecord from "models/view-models/user-record";
import ServiceFactory from "utilities/services/service-factory";
import ServiceHookFactory from "utilities/services/service-hook-factory";
import CreateUserDtoRecord from "models/view-models/create-user-dto-record";
import QueryHookFactory from "utilities/hooks/queries/query-hook-factory";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface UserBasePathParams {}

export interface UserResourcePathParams extends UserBasePathParams {
    id: number;
}

/**
 * Search parameters when listing User resources
 * These get translated to QueryString parameters
 */
export interface UserListQueryParams {
    /**
     * An array of userIds to filter by.
     */
    ids: number[];
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const baseEndpoint = "users";

const resourceType = UserRecord;
const createResourceType = CreateUserDtoRecord;
const resourceEndpoint = `${baseEndpoint}/:id`;
const key = "users";
const completedFavoritesOnboardingEndpoint = baseEndpoint + "/completed";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Service
// -----------------------------------------------------------------------------------------

const UserService = {
    /**
     * Create a new user
     */
    create: ServiceFactory.create(createResourceType, baseEndpoint),

    /**
     * Soft delete a user entity
     */
    delete: ServiceFactory.delete(resourceEndpoint),

    /**
     * Retrieve a user by id
     */
    get: ServiceFactory.get<UserRecord, UserResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),

    /**
     * Retrieve a a list of all users matching given parameters
     */
    list: ServiceFactory.list<UserRecord, UserListQueryParams>(
        resourceType,
        baseEndpoint
    ),

    /**
     * Update user details
     */
    update: ServiceFactory.update(resourceType, resourceEndpoint),

    /**
     * Update user details
     */
    completed: ServiceFactory.patch(
        resourceType,
        completedFavoritesOnboardingEndpoint
    ),

    /**
     * Custom hook for leveraging service create calls in react components
     */
    useCreate: ServiceHookFactory.useCreate(createResourceType, baseEndpoint),

    /**
     * Custom hook for leveraging service delete calls in react components
     */
    useDelete: ServiceHookFactory.useDelete(resourceEndpoint),

    useBatchList: ServiceHookFactory.useBatchedList<
        UserRecord,
        UserListQueryParams
    >(resourceType, baseEndpoint),

    useBatchListQuery: QueryHookFactory.useBatchedListQuery<
        UserRecord,
        UserListQueryParams
    >(key, resourceType, baseEndpoint),

    /**
     * Custom hook for leveraging service get calls in react components
     */
    useGet: ServiceHookFactory.useGet<UserRecord, UserResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),
    useGetQuery: QueryHookFactory.useGetQuery<
        UserRecord,
        UserResourcePathParams
    >(key, resourceType, resourceEndpoint),

    /**
     * Custom hook for leveraging service list calls in react components
     */
    useList: ServiceHookFactory.useList<UserRecord, UserListQueryParams>(
        resourceType,
        baseEndpoint
    ),
    useListQuery: QueryHookFactory.useListQuery<
        UserRecord,
        UserListQueryParams
    >(key, resourceType, baseEndpoint),

    /**
     * Custom hook for updating the EULA details for a user
     */
    useUpdate: ServiceHookFactory.useUpdate<UserRecord, UserResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),
    /**
     * Custom hook for updating the EULA details for a user
     */
    useOnboardingFavoritesCompletedOn: ServiceHookFactory.usePatch<
        UserRecord,
        UserResourcePathParams
    >(resourceType, completedFavoritesOnboardingEndpoint),
};

// #endregion Service

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default UserService;

// #endregion Exports
